// SNIGJ - Admin Cash Overview Page
// Global view of all cash registers by entity/structure
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { t } from '../lib/i18n';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import { 
  Building2, Wallet, TrendingUp, TrendingDown, AlertTriangle, CheckCircle,
  Clock, DollarSign, Users, MapPin, Calendar, Download, RefreshCw,
  BarChart3, PieChart as PieChartIcon, FileText, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// DRC Provinces
const PROVINCES = [
  "KINSHASA", "KONGO-CENTRAL", "KWANGO", "KWILU", "MAI-NDOMBE",
  "KASAI", "KASAI-CENTRAL", "KASAI-ORIENTAL", "LOMAMI", "SANKURU",
  "MANIEMA", "SUD-KIVU", "NORD-KIVU", "ITURI", "HAUT-UELE",
  "TSHOPO", "BAS-UELE", "NORD-UBANGI", "SUD-UBANGI", "MONGALA",
  "EQUATEUR", "TSHUAPA", "TANGANYIKA", "HAUT-LOMAMI", "LUALABA", "HAUT-KATANGA"
];

// Institution types
const INSTITUTION_TYPES = [
  { value: 'TRIBUNAL_PAIX', label: { fr: 'Tribunaux de Paix', en: 'Peace Courts' } },
  { value: 'PARQUET_GI', label: { fr: 'Parquets Grande Instance', en: 'High Courts' } },
  { value: 'COUR_APPEL', label: { fr: 'Cours d\'Appel', en: 'Appeal Courts' } },
  { value: 'PRISON', label: { fr: 'Prisons', en: 'Prisons' } },
  { value: 'COMMISSARIAT', label: { fr: 'Commissariats', en: 'Police Stations' } }
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const AdminCashOverviewPage = () => {
  const { user, language } = useAuth();
  const { displayAmount, formatCurrency } = useCurrency();
  
  const [overviewData, setOverviewData] = useState({
    total_national: 0,
    total_collected: 0,
    total_pending: 0,
    total_overdue: 0,
    collection_rate: 0,
    by_province: [],
    by_institution: [],
    by_agent: [],
    trends: []
  });
  const [loading, setLoading] = useState(true);
  const [filterProvince, setFilterProvince] = useState('all');
  const [filterInstitution, setFilterInstitution] = useState('all');
  const [period, setPeriod] = useState('month');

  useEffect(() => {
    loadOverviewData();
  }, [filterProvince, filterInstitution, period]);

  const loadOverviewData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/cash-overview', {
        params: { province: filterProvince, institution: filterInstitution, period }
      });
      setOverviewData(response.data || generateMockData());
    } catch (error) {
      console.error('Failed to load overview:', error);
      setOverviewData(generateMockData());
    } finally {
      setLoading(false);
    }
  };

  // Generate mock data for demo
  const generateMockData = () => {
    const provinceData = PROVINCES.slice(0, 10).map(prov => ({
      province: prov,
      billed: Math.floor(Math.random() * 50000) + 10000,
      collected: Math.floor(Math.random() * 40000) + 5000,
      pending: Math.floor(Math.random() * 10000) + 1000,
      overdue: Math.floor(Math.random() * 5000) + 500,
      agents: Math.floor(Math.random() * 20) + 5
    }));

    const institutionData = INSTITUTION_TYPES.map(inst => ({
      type: inst.value,
      label: inst.label[language],
      billed: Math.floor(Math.random() * 100000) + 20000,
      collected: Math.floor(Math.random() * 80000) + 15000,
      rate: Math.floor(Math.random() * 30) + 60
    }));

    const agentData = [
      { name: 'Jean Mukeba', structure: 'TGI Kinshasa', billed: 15000, collected: 12500, pending: 2500 },
      { name: 'Marie Kabongo', structure: 'Parquet Lubumbashi', billed: 12000, collected: 11000, pending: 1000 },
      { name: 'Pierre Nkunda', structure: 'Tribunal Goma', billed: 8500, collected: 7000, pending: 1500 },
      { name: 'Alice Mutombo', structure: 'Cour Appel Kisangani', billed: 20000, collected: 18500, pending: 1500 },
      { name: 'David Kasongo', structure: 'Prison Makala', billed: 5000, collected: 4200, pending: 800 }
    ];

    const trends = [
      { month: 'Jan', billed: 120000, collected: 95000 },
      { month: 'Feb', billed: 135000, collected: 110000 },
      { month: 'Mar', billed: 150000, collected: 125000 },
      { month: 'Apr', billed: 145000, collected: 130000 },
      { month: 'May', billed: 160000, collected: 145000 },
      { month: 'Jun', billed: 175000, collected: 155000 }
    ];

    const totalBilled = provinceData.reduce((sum, p) => sum + p.billed, 0);
    const totalCollected = provinceData.reduce((sum, p) => sum + p.collected, 0);

    return {
      total_national: totalBilled,
      total_collected: totalCollected,
      total_pending: provinceData.reduce((sum, p) => sum + p.pending, 0),
      total_overdue: provinceData.reduce((sum, p) => sum + p.overdue, 0),
      collection_rate: (totalCollected / totalBilled * 100).toFixed(1),
      by_province: provinceData,
      by_institution: institutionData,
      by_agent: agentData,
      trends
    };
  };

  // Filter province data
  const filteredProvinces = overviewData.by_province.filter(p => 
    filterProvince === 'all' || p.province === filterProvince
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-cash-overview">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-primary" />
              {language === 'fr' ? 'Vue Globale des Caisses' : 'Global Cash Overview'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' 
                ? 'Synthèse des encaissements par entité administrative'
                : 'Collection summary by administrative entity'}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[130px]">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">{language === 'fr' ? 'Semaine' : 'Week'}</SelectItem>
                <SelectItem value="month">{language === 'fr' ? 'Mois' : 'Month'}</SelectItem>
                <SelectItem value="quarter">{language === 'fr' ? 'Trimestre' : 'Quarter'}</SelectItem>
                <SelectItem value="year">{language === 'fr' ? 'Année' : 'Year'}</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadOverviewData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button variant="outline" className="gap-2">
              <Download className="w-4 h-4" />
              {language === 'fr' ? 'Exporter' : 'Export'}
            </Button>
          </div>
        </div>

        {/* National Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <DollarSign className="w-8 h-8 opacity-80" />
                <div>
                  <p className="text-sm opacity-80">{language === 'fr' ? 'Total National Facturé' : 'National Total Billed'}</p>
                  <p className="text-2xl font-bold">{displayAmount(overviewData.total_national, 'USD')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-8 h-8 opacity-80" />
                <div>
                  <p className="text-sm opacity-80">{language === 'fr' ? 'Total Encaissé' : 'Total Collected'}</p>
                  <p className="text-2xl font-bold">{displayAmount(overviewData.total_collected, 'USD')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 opacity-80" />
                <div>
                  <p className="text-sm opacity-80">{language === 'fr' ? 'En Attente' : 'Pending'}</p>
                  <p className="text-2xl font-bold">{displayAmount(overviewData.total_pending, 'USD')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 opacity-80" />
                <div>
                  <p className="text-sm opacity-80">{language === 'fr' ? 'Impayés' : 'Overdue'}</p>
                  <p className="text-2xl font-bold">{displayAmount(overviewData.total_overdue, 'USD')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-8 h-8 opacity-80" />
                <div>
                  <p className="text-sm opacity-80">{language === 'fr' ? 'Taux Recouvrement' : 'Collection Rate'}</p>
                  <p className="text-2xl font-bold">{overviewData.collection_rate}%</p>
                </div>
              </div>
              <Progress value={parseFloat(overviewData.collection_rate) || 0} className="mt-2 h-2 bg-white/30" />
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
          <Select value={filterProvince} onValueChange={setFilterProvince}>
            <SelectTrigger className="w-[200px]">
              <MapPin className="w-4 h-4 mr-2" />
              <SelectValue placeholder={language === 'fr' ? 'Province' : 'Province'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{language === 'fr' ? 'Toutes les provinces' : 'All provinces'}</SelectItem>
              {PROVINCES.map(prov => (
                <SelectItem key={prov} value={prov}>{prov}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filterInstitution} onValueChange={setFilterInstitution}>
            <SelectTrigger className="w-[200px]">
              <Building2 className="w-4 h-4 mr-2" />
              <SelectValue placeholder={language === 'fr' ? 'Type institution' : 'Institution type'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{language === 'fr' ? 'Toutes institutions' : 'All institutions'}</SelectItem>
              {INSTITUTION_TYPES.map(inst => (
                <SelectItem key={inst.value} value={inst.value}>{inst.label[language]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tabs with Charts */}
        <Tabs defaultValue="provinces" className="space-y-4">
          <TabsList>
            <TabsTrigger value="provinces" className="gap-2">
              <MapPin className="w-4 h-4" />
              {language === 'fr' ? 'Par Province' : 'By Province'}
            </TabsTrigger>
            <TabsTrigger value="institutions" className="gap-2">
              <Building2 className="w-4 h-4" />
              {language === 'fr' ? 'Par Institution' : 'By Institution'}
            </TabsTrigger>
            <TabsTrigger value="agents" className="gap-2">
              <Users className="w-4 h-4" />
              {language === 'fr' ? 'Par Agent' : 'By Agent'}
            </TabsTrigger>
            <TabsTrigger value="trends" className="gap-2">
              <TrendingUp className="w-4 h-4" />
              {language === 'fr' ? 'Tendances' : 'Trends'}
            </TabsTrigger>
          </TabsList>

          {/* By Province */}
          <TabsContent value="provinces">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>{language === 'fr' ? 'Encaissements par Province' : 'Collections by Province'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={filteredProvinces.slice(0, 8)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="province" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                      <YAxis />
                      <Tooltip formatter={(value) => formatCurrency(value, 'USD')} />
                      <Legend />
                      <Bar dataKey="billed" name={language === 'fr' ? 'Facturé' : 'Billed'} fill="#3b82f6" />
                      <Bar dataKey="collected" name={language === 'fr' ? 'Encaissé' : 'Collected'} fill="#22c55e" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{language === 'fr' ? 'Détail par Province' : 'Province Detail'}</CardTitle>
                </CardHeader>
                <CardContent className="max-h-[400px] overflow-auto">
                  <div className="space-y-3">
                    {filteredProvinces.map((prov, idx) => {
                      const rate = ((prov.collected / prov.billed) * 100).toFixed(1);
                      return (
                        <div key={prov.province} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{idx + 1}</Badge>
                              <span className="font-medium">{prov.province}</span>
                            </div>
                            <Badge className={parseFloat(rate) >= 80 ? 'bg-green-500' : parseFloat(rate) >= 60 ? 'bg-yellow-500' : 'bg-red-500'}>
                              {rate}%
                            </Badge>
                          </div>
                          <div className="grid grid-cols-4 gap-2 text-sm">
                            <div>
                              <p className="text-muted-foreground">{language === 'fr' ? 'Facturé' : 'Billed'}</p>
                              <p className="font-medium">{displayAmount(prov.billed, 'USD')}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-green-600">{language === 'fr' ? 'Encaissé' : 'Collected'}</p>
                              <p className="font-medium text-green-600">{displayAmount(prov.collected, 'USD')}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-yellow-600">{language === 'fr' ? 'Attente' : 'Pending'}</p>
                              <p className="font-medium text-yellow-600">{displayAmount(prov.pending, 'USD')}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-red-600">{language === 'fr' ? 'Impayé' : 'Overdue'}</p>
                              <p className="font-medium text-red-600">{displayAmount(prov.overdue, 'USD')}</p>
                            </div>
                          </div>
                          <Progress value={parseFloat(rate)} className="mt-2 h-2" />
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* By Institution */}
          <TabsContent value="institutions">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>{language === 'fr' ? 'Répartition par Type' : 'Distribution by Type'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie
                        data={overviewData.by_institution}
                        dataKey="collected"
                        nameKey="label"
                        cx="50%"
                        cy="50%"
                        outerRadius={120}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {overviewData.by_institution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value, 'USD')} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{language === 'fr' ? 'Performance par Institution' : 'Institution Performance'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {overviewData.by_institution.map((inst, idx) => (
                      <div key={inst.type} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{inst.label}</span>
                          <span className="text-sm text-muted-foreground">
                            {displayAmount(inst.collected, 'USD')} / {displayAmount(inst.billed, 'USD')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Progress value={inst.rate} className="flex-1 h-3" />
                          <Badge variant="outline">{inst.rate}%</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* By Agent */}
          <TabsContent value="agents">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'fr' ? 'Performance des Agents' : 'Agent Performance'}</CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Classement des agents par volume d\'encaissement'
                    : 'Agent ranking by collection volume'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {overviewData.by_agent.map((agent, idx) => {
                    const rate = ((agent.collected / agent.billed) * 100).toFixed(1);
                    return (
                      <div key={agent.name} className="flex items-center gap-4 p-4 border rounded-lg">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{agent.name}</p>
                          <p className="text-sm text-muted-foreground">{agent.structure}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-600">{displayAmount(agent.collected, 'USD')}</p>
                          <p className="text-xs text-muted-foreground">/ {displayAmount(agent.billed, 'USD')}</p>
                        </div>
                        <div className="w-24">
                          <Progress value={parseFloat(rate)} className="h-2" />
                          <p className="text-xs text-center mt-1">{rate}%</p>
                        </div>
                        <Button variant="outline" size="sm">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trends */}
          <TabsContent value="trends">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'fr' ? 'Évolution des Encaissements' : 'Collection Trends'}</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={overviewData.trends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value, 'USD')} />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="billed" 
                      name={language === 'fr' ? 'Facturé' : 'Billed'}
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      dot={{ fill: '#3b82f6' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="collected" 
                      name={language === 'fr' ? 'Encaissé' : 'Collected'}
                      stroke="#22c55e" 
                      strokeWidth={2}
                      dot={{ fill: '#22c55e' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AdminCashOverviewPage;
